// === SAA:UI:PREMIUM-BREAKDOWN-CARD:v4 ===
// Two-tier total card: primary monthly amount on top with a single "per maand" label, secondary
// "Eerste betaling" amount underneath (only when a one-time poliskosten applies). All small-print
// disclaimers (tax / one-time fee detail) consolidate into a single footnote pinned to the bottom
// of the card so the numbers breathe. Per-product + fee breakdown always renders expanded (no
// toggle) and uses one subtle divider — separation otherwise comes from whitespace + typographic
// hierarchy. Used on summary, payment-information, acceptance-questions, and confirmation so the
// displayed price is identical across the flow.

import React from 'react';
import {
  useWorkflow,
  useConfig,
  formatPrice,
  computeAvpNetPremium,
  computeWoonhuisNetPremium,
  computePersonenautoNetPremium,
  computeRechtsbijstandNetPremium,
  computeAvpDisplayPremium,
  computeWoonhuisDisplayPremium,
  computePersonenautoDisplayPremium,
  computeRechtsbijstandDisplayPremium,
  composePremiumBreakdown,
} from '@sluitstraat/core';
import { computeInboedelNetFromSections, computeInboedelTotalFromSections } from '../utils/inboedelPricing';
import { getProductContent } from '../products/content';
import type { SaaProductId } from '../utils/addressSharing';
import { computeDoorlopendeReisDisplayPremium } from '../utils/doorlopendeReisPricing';
import { computePolicyCost } from '../utils/policyCost';
import { computeSubscriptionFee, subscriptionTaxPercentLabel } from '../utils/subscriptionFee';
import { getProductOrder } from '../workflowConfig';
import { productIcons, ProductIconId } from '../icons/productRegistry';
import { useTranslation } from 'react-i18next';
import { premiumBreakdownStyles as styles } from '../styles/commonStyles';

interface PremiumBreakdownCardProps {
  /** Hide the spinner and assume endpoints already settled (used on screens after summary). */
  skipChainGate?: boolean;
  /**
   * When true, the 21% assurantiebelasting renders as its own line. When false (default),
   * tax is folded into the per-product net lines and the tax row is omitted — the grand
   * total is unchanged either way. Driven by `config.showTaxSeparate.enabled` on the
   * summary screen; other call sites can pass the prop explicitly.
   */
  showTaxSeparate?: boolean;
}

export const PremiumBreakdownCard: React.FC<PremiumBreakdownCardProps> = ({ skipChainGate = false, showTaxSeparate: showTaxSeparateProp }) => {
  const { t } = useTranslation();
  const { data, selectedProducts } = useWorkflow();
  const { config, isLoading: isConfigLoading } = useConfig();

  // Maandabonnement carries its own 5% tax (separate from the 21% assurantiebelasting), so we
  // display it incl. tax and surface the 5% on its own breakdown line. See utils/subscriptionFee.
  const subscription = computeSubscriptionFee(selectedProducts, config);
  const subscriptionFee = subscription.withTax;
  const prolongationFeeConfig = (config as any)?.prolongationFee;
  const prolongationFee = prolongationFeeConfig?.enabled ? (prolongationFeeConfig.amount || 2.50) : 0;
  const policyCost = computePolicyCost(selectedProducts, config);

  // Tax-display mode: prop > config > default (false → tax folded into net lines, no separate row).
  const showTaxSeparateConfig = (config as any)?.showTaxSeparate;
  const showTaxSeparate = showTaxSeparateProp ?? (showTaxSeparateConfig?.enabled === true);

  // Loading gate. Summary uses it to hide the breakdown until the relation/package chain finishes;
  // downstream screens (payment, acceptance, confirmation) skip the gate because the chain is
  // already complete by the time they render.
  const chainComplete = skipChainGate || sessionStorage.getItem('package_discount_applied') === 'true';
  if (isConfigLoading || !chainComplete) {
    return (
      <div style={styles.loadingColumn}>
        <div style={styles.loadingSpinner}></div>
        <span style={styles.loadingText}>
          {t('screens.offerSummary.loadingPremium', { defaultValue: 'Premie wordt berekend...' })}
        </span>
      </div>
    );
  }

  // Source-PP picker: prefer enriched packageProduct (with marker injected so the
  // helpers don't re-apply the discount) for products where the package data is more
  // complete, fall back to workflow data. AVP always uses workflow (BFF rounds DA_BTP).
  let pkgProducts: any[] = [];
  try {
    const enrichedDoc = sessionStorage.getItem('enriched_calculation_document');
    if (enrichedDoc) {
      const doc = JSON.parse(enrichedDoc);
      const pakketOnderdeel = doc?.Pakket?.[0]?.Onderdeel || [];
      pkgProducts = pakketOnderdeel.length > 0 ? pakketOnderdeel : (doc?.Contract || []);
    }
  } catch (_) { /* fall back to workflow data */ }

  // Product icons come from the central productRegistry — the same map as the
  // selection cards and the sidebar, so surfaces can no longer diverge. The
  // IconKey glyphs below remain only for the FEE rows (incasso / abonnement / tax),
  // which are not products and have no registry entry.
  type IconKey = 'refresh' | 'calendar' | 'percent';
  const lines: { id: string; label: string; net: { netPremium: number; netPremiumPrecise: number }; display: number }[] = [];

  const addLine = (
    id: SaaProductId,
    compute: (productData: any) => { net: { netPremium: number; netPremiumPrecise: number }; display: number },
  ) => {
    if (!selectedProducts?.[id]) return;
    const { net, display } = compute(data?.[id]);
    if (net.netPremium > 0) {
      lines.push({ id, label: t(getProductContent(id).offerNameKey), net, display });
    }
  };

  addLine('personenauto', (pd) => ({
    net: computePersonenautoNetPremium(pd, config as any),
    display: computePersonenautoDisplayPremium(pd, config as any),
  }));
  addLine('woonhuis', (pd) => {
    // Use the workflow data, not packageProduct.PP — the latter re-applies the package
    // discount and would double-discount this per-product line (the discount has its own row).
    const pkg = pkgProducts.find((p: any) => p.PP?.OP && !p.PP?.IP && !p.PP?.DA);
    const source = pkg?.PP?.PP_BTP || pkg?.PP?.OP?.OP_BTP
      ? { Contractdocument: { PP: { ...pkg.PP, _discountBaked: true } } }
      : pd;
    return {
      net: computeWoonhuisNetPremium(source, config as any),
      display: computeWoonhuisDisplayPremium(source, config as any),
    };
  });
  addLine('inboedel', (pd) => ({
    net: computeInboedelNetFromSections(pd, config as any),
    display: computeInboedelTotalFromSections(pd, config as any),
  }));
  addLine('aansprakelijkheid', (pd) => ({
    net: computeAvpNetPremium(pd, config as any),
    display: computeAvpDisplayPremium(pd, config as any),
  }));
  addLine('rechtsbijstand', (pd) => {
    const rbDekkingen = pd?.Supportsection?.Dekkingen || {};
    return {
      net: computeRechtsbijstandNetPremium(pd, config as any, rbDekkingen),
      display: computeRechtsbijstandDisplayPremium(pd, config as any, rbDekkingen),
    };
  });

  // Order the product lines by the configured product order (config.json) so the cart
  // matches the workflow/page order. Pure reorder — totals are sum-based, order-independent.
  // Doorlopende reis renders in its own tax group below (no assurantiebelasting), so it is
  // not part of this 21% list.
  const order = getProductOrder();
  lines.sort((a, b) => order.indexOf(a.id as any) - order.indexOf(b.id as any));

  // Both DFD products are tax-exempt and render in the separate group below. A bare OD
  // section is the Ongevallen product; DR (with or without a legacy OD) is doorlopendereis.
  const drPP = selectedProducts?.doorlopendereis
    ? (pkgProducts.find((p: any) => p.PP?.DR)?.PP || data?.['doorlopendereis']?.Contractdocument?.PP)
    : null;
  const drDisplay = drPP ? computeDoorlopendeReisDisplayPremium(drPP) : 0;
  const ongevallenPP = selectedProducts?.ongevallen
    ? (pkgProducts.find((p: any) => p.PP?.OD && !p.PP?.DR)?.PP || data?.['ongevallen']?.Contractdocument?.PP)
    : null;
  const ongevallenDisplay = ongevallenPP ? computeDoorlopendeReisDisplayPremium(ongevallenPP) : 0;

  // Registry icon at the cart's row size, inheriting the row's text colour.
  const renderProductIcon = (id: string) => {
    const Icon = productIcons[id as ProductIconId];
    return Icon ? <Icon size={16} weight="duotone" aria-hidden="true" /> : null;
  };

  const renderLineIcon = (key: IconKey) => {
    const common = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 } as const;
    switch (key) {
      // Circular arrow — fits the recurring/collection nature of Incassokosten.
      case 'refresh': return (<svg {...common}><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>);
      // Calendar — Maandabonnement is a monthly cadence; calendar reads as recurring.
      case 'calendar': return (<svg {...common}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>);
      case 'percent': return (<svg {...common}><line x1="19" y1="5" x2="5" y2="19"></line><circle cx="6.5" cy="6.5" r="2.5"></circle><circle cx="17.5" cy="17.5" r="2.5"></circle></svg>);
    }
  };

  if (lines.length === 0 && drDisplay <= 0 && ongevallenDisplay <= 0) return null;
  const breakdown = composePremiumBreakdown(lines.map(l => l.net), prolongationFee);

  const lineDisplayedValues = lines.map(l =>
    showTaxSeparate ? l.net.netPremium : l.display,
  );

  // Unified product rows in configured product order. Doorlopende Reis is tax-free (no 21% fold),
  // but must sit among the other products in product order — not in a trailing group below the fees.
  // Its tax-free status is communicated by its own footnote below, not by position.
  const productRows: { id: string; label: string; displayed: number }[] = lines.map((l, idx) => ({
    id: l.id, label: l.label, displayed: lineDisplayedValues[idx],
  }));
  if (ongevallenDisplay > 0) {
    productRows.push({
      id: 'ongevallen',
      label: t(getProductContent('ongevallen').offerNameKey),
      displayed: ongevallenDisplay,
    });
  }
  if (drDisplay > 0) {
    productRows.push({
      id: 'doorlopendereis',
      label: t(getProductContent('doorlopendereis').offerNameKey),
      displayed: drDisplay,
    });
  }
  productRows.sort((a, b) => order.indexOf(a.id as any) - order.indexOf(b.id as any));
  const incassoDisplayed = showTaxSeparate
    ? breakdown.collectionFee
    : Math.floor(breakdown.collectionFee * 1.21 * 100) / 100;
  const sumOfLines = lineDisplayedValues.reduce((s, v) => s + v, 0);
  const displayTotal = (showTaxSeparate
    ? breakdown.total + subscriptionFee
    : sumOfLines + incassoDisplayed + subscriptionFee) + drDisplay + ongevallenDisplay;

  const firstPaymentTotal = displayTotal + policyCost.withTax;
  const hasOneTimeFee = policyCost.withTax > 0;

  // Breakdown row factory — shared by product lines, fees, and tax so they all carry the same
  // icon size / spacing / colour. Eliminates the old italic-grey treatment for fees: when the
  // breakdown is expanded the customer wants a clean list, not visually-second-class rows.
  const renderBreakdownRow = (icon: React.ReactNode, label: string, amount: number, key: string) => (
    <div key={key} style={styles.row}>
      <span style={styles.rowLabel}>
        {icon}
        <span style={styles.rowLabelText}>{label}</span>
      </span>
      <span style={styles.rowLabelText}>€ {formatPrice(amount)}</span>
    </div>
  );

  // Per-group VAT footnote — same small muted treatment for the 21% (products/fees) and the 5%
  // (subscription) groups so they read as the same kind of disclaimer, only the rate differs.
  const renderTaxFootnote = (text: string, key: string) => (
    <div key={key} style={styles.taxFootnote}>
      {text}
    </div>
  );

  return (
    <div style={styles.card}>
      {/* Primary total — recurring monthly amount sits at the top because "what does this cost
          per month?" is what customers compare on. Left-aligned with the header so the title +
          price + label read down a single axis. */}
      <div style={styles.totalBlock}>
        <div style={styles.primaryAmount}>
          € {formatPrice(displayTotal)}
        </div>
        <div style={styles.perMonth}>
          {t('screens.offerSummary.perMonth', { defaultValue: 'per maand' })}
        </div>
      </div>

      {/* Secondary total — first-payment context (recurring + one-time poliskosten). Markedly
          smaller and lighter than the primary so the eye lands on the monthly figure first. */}
      {hasOneTimeFee && (
        <div style={styles.firstPaymentBlock}>
          <div style={styles.firstPaymentAmount}>
            € {formatPrice(firstPaymentTotal)}
          </div>
          <div style={styles.firstPaymentLabel}>
            {t('screens.offerSummary.firstPayment', { defaultValue: 'eerste betaling' })}
          </div>
          {/* One-time policy cost kept tight against the first-payment number so it's obvious
              which line item this disclaimer relates to. "(incl. btw)" omitted — the bottom
              footnote already says "Inclusief 21% assurantiebelasting". */}
          <div style={styles.policyCostNote}>
            {t('screens.offerSummary.includesPolicyCostInline', {
              defaultValue: 'Eenmalige poliskosten: € {{withTax}}',
              withTax: formatPrice(policyCost.withTax),
            })}
          </div>
        </div>
      )}

      {/* Single subtle line above the breakdown is the only divider in the card — everything
          else relies on whitespace and typographic hierarchy to separate sections. The body
          splits into two VAT zones: the 21% group (products + fees) and the 5% group
          (maandabonnement), each closed off by its own muted footnote rather than a tax line. */}
      <div style={styles.breakdownBody}>
          {/* Group 1 — products and fees taxed at 21%. Amounts already incl. VAT in the default
              folded mode; the footnote states the rate. In showTaxSeparate mode the 21% surfaces
              as its own line instead and the footnote is omitted. */}
          <div style={styles.group}>
            {productRows.map((r) => renderBreakdownRow(renderProductIcon(r.id), r.label, r.displayed, `prod-${r.id}`))}
            {prolongationFee > 0 && renderBreakdownRow(
              renderLineIcon('refresh'),
              t('screens.offerSummary.serviceFee', { defaultValue: 'Incassokosten' }),
              incassoDisplayed,
              'incasso',
            )}
            {showTaxSeparate
              ? (lines.length > 0 && renderBreakdownRow(
                  renderLineIcon('percent'),
                  t('screens.offerSummary.insuranceTax', { defaultValue: 'Assurantiebelasting (21%)' }),
                  breakdown.tax,
                  'tax',
                ))
              // Folded mode: keep the single "Inclusief 21% assurantiebelasting" footnote for the whole
              // list (products + Incassokosten). Doorlopende Reis is shown as a normal line, so the note
              // renders whenever any product row is present — not only when a 21% product is selected.
              : (productRows.length > 0 && renderTaxFootnote(
                  t('screens.offerSummary.includingInsuranceTax', { defaultValue: 'Inclusief 21% assurantiebelasting' }),
                  'tax-footnote',
                ))}
          </div>

          {/* Group 2 — monthly subscription taxed at 5%. The €0,63 VAT is folded into the
              displayed amount (net × 1.05) so it reads incl. VAT, mirroring group 1; the rate
              is communicated by the footnote below rather than a separate line. */}
          {subscription.net > 0 && (
            <div style={styles.group}>
              {renderBreakdownRow(
                renderLineIcon('calendar'),
                t('screens.offerSummary.monthlySubscription', { defaultValue: 'Serviceabonnement' }),
                subscription.withTax,
                'sub',
              )}
              {renderTaxFootnote(
                t('screens.offerSummary.includingSubscriptionTax', {
                  percent: subscriptionTaxPercentLabel(subscription.taxRate),
                  defaultValue: `Inclusief ${subscriptionTaxPercentLabel(subscription.taxRate)}% assurantiebelasting`,
                }),
                'sub-tax-footnote',
              )}
            </div>
          )}
      </div>
    </div>
  );
};
